import {
  type SearchType,
  usePermissions,
  usePreference,
  useUser,
} from '@tyro/api';
import {
  type UseDisclosureReturn,
  useDebouncedValue,
  useDisclosure,
} from '@tyro/core';
import { useLocation } from '@tyro/router/hooks';
import {
  type ReactNode,
  Suspense,
  createContext,
  lazy,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { MailTypeMessage } from '../@types/compose-mail';
import type { MailLabel } from '../@types/label';
import { useUnreadCount } from '../api/labels';
import { MailAdvancedSearchDrawer } from '../components/common/advanced-search-drawer';
import type { MailComposeProps } from '../components/common/compose';
import {
  type MailRecipientType,
  type RecipientsForMailModal,
  SelectMailRecipientTypeModal,
} from '../components/common/select-mail-recipient-type-modal';

const MailCompose = lazy(() => import('../components/common/compose'));
import { useResolveRecipients } from '../hooks/use-resolve-recipients';
import { getLabelId } from '../utils/labels';

export type MailSettingsContextValue = {
  sidebarDisclosure: UseDisclosureReturn;
  composeDisclosure: UseDisclosureReturn;
  openAdvancedSearch: () => void;
  activeProfileId: number;
  composeEmail: (defaultValue: MailComposeProps['defaultValues']) => void;
  sendMailToParties: (
    parties: RecipientsForMailModal,
    possibleMailRecipientTypes: MailRecipientType[],
  ) => void;
  hasPermissionForSchoolInbox: boolean;
  hasPermissionForRulesInbox: boolean;
  isSchoolMail: boolean;
  isRulesMail: boolean;
  isScheduledMailList: boolean;
  partyUnreadCount: number;
  schoolUnreadCount: number;
  rulesUnreadCount: number;
  getUnreadCount: (label: Pick<MailLabel, 'type' | 'id'>) => number;
};

const MailSettingsContext = createContext<MailSettingsContextValue | undefined>(
  undefined,
);

function useCurrentProfilePartyId() {
  const { activeProfile } = useUser();
  const location = useLocation();

  if (location.pathname.includes('mail/school')) {
    return -1;
  }

  if (location.pathname.includes('mail/rules')) {
    return -2;
  }

  return activeProfile?.partyId ?? 0;
}

export function MailSettingsProvider({ children }: { children: ReactNode }) {
  const [defaultComposeValue, setDefaultComposeValue] = useState<
    MailComposeProps['defaultValues']
  >({});
  const [_preferredMail, setPreferredMail] = usePreference(
    'mail.preferred-mail',
  );
  const sidebarDisclosure = useDisclosure({ defaultIsOpen: false });
  const composeDisclosure = useDisclosure({ defaultIsOpen: false });
  const advancedSearchDisclosure = useDisclosure({ defaultIsOpen: false });
  const location = useLocation();
  const {
    value: selectMailRecipientSettings,
    debouncedValue: debouncedSelectMailRecipientSettings,
    setValue: setSelectMailRecipientSettings,
  } = useDebouncedValue<
    | {
        parties: RecipientsForMailModal;
        possibleRecipientTypes: MailRecipientType[];
      }
    | undefined
  >({ defaultValue: undefined });
  const { activeProfile } = useUser();
  const { hasPermission, isStaffUserWithPermission } = usePermissions();
  const activeProfileId = useCurrentProfilePartyId();
  const { resolveMailRecipients } = useResolveRecipients();
  const isScheduledMailList =
    location.pathname.includes('mail/scheduled') ||
    location.pathname.includes('mail/school/scheduled') ||
    location.pathname.includes('mail/rules/scheduled');

  const { data: unreadCountData } = useUnreadCount({
    personPartyId: activeProfile?.partyId ?? 0,
  });

  const hasPermissionForSchoolInbox = hasPermission(
    'ps:1:communications:school_mailbox',
  );
  const hasPermissionForRulesInbox = isStaffUserWithPermission(
    'ps:1:users:rules_engine',
  );

  const composeEmail = (defaultValue: MailComposeProps['defaultValues']) => {
    setDefaultComposeValue(defaultValue);
    composeDisclosure.onOpen();
  };

  const composeEmailToParties = async (
    parties: RecipientsForMailModal,
    possibleMailRecipientTypes: SearchType[],
  ) => {
    const communicationsRecipients = resolveMailRecipients(
      parties,
      possibleMailRecipientTypes,
    );

    if (selectMailRecipientSettings) {
      setSelectMailRecipientSettings(undefined);
    }
    composeEmail({
      canReply: false,
      typeMessage: MailTypeMessage.BROADCAST,
      toRecipients: communicationsRecipients,
    });
  };

  const sendMailToParties = (
    parties: RecipientsForMailModal,
    possibleMailRecipientTypes: MailRecipientType[],
  ) => {
    if (
      process.env.NODE_ENV !== 'production' &&
      !(parties?.length > 0 && possibleMailRecipientTypes?.length > 0)
    ) {
      throw new Error(
        'partyIds and possibleMailRecipientTypes must not be empty when calling sendMailToParties',
      );
    }

    if (possibleMailRecipientTypes.length === 1) {
      composeEmailToParties(parties, [possibleMailRecipientTypes[0].type]);
    } else {
      setSelectMailRecipientSettings({
        parties,
        possibleRecipientTypes: possibleMailRecipientTypes,
      });
    }
  };

  useEffect(() => {
    if (
      hasPermissionForSchoolInbox &&
      location.pathname.includes('mail/school')
    ) {
      setPreferredMail('school');
    } else if (location.pathname.includes('mail')) {
      setPreferredMail(undefined);
    }
  }, [location]);

  const value = useMemo<MailSettingsContextValue>(
    () => ({
      sidebarDisclosure,
      composeDisclosure,
      openAdvancedSearch: advancedSearchDisclosure.onOpen,
      composeEmail,
      activeProfileId,
      sendMailToParties,
      hasPermissionForSchoolInbox,
      hasPermissionForRulesInbox,
      isSchoolMail: activeProfileId === -1,
      isRulesMail: activeProfileId === -2,
      isScheduledMailList,
      getUnreadCount: (label) => {
        const labelId = getLabelId(label);
        return unreadCountData?.get(`${activeProfileId}-${labelId}`) ?? 0;
      },
      partyUnreadCount:
        unreadCountData?.get(`${activeProfile?.partyId ?? 0}-inbox`) ?? 0,
      schoolUnreadCount: unreadCountData?.get(`${-1}-inbox`) ?? 0,
      rulesUnreadCount: unreadCountData?.get(`${-2}-inbox`) ?? 0,
    }),
    [
      sidebarDisclosure,
      composeDisclosure,
      advancedSearchDisclosure.onOpen,
      composeEmail,
      activeProfileId,
      isScheduledMailList,
      sendMailToParties,
      hasPermissionForSchoolInbox,
      hasPermissionForRulesInbox,
      unreadCountData,
    ],
  );

  useEffect(() => {
    if (!composeDisclosure.isOpen) {
      sidebarDisclosure.onClose();
    }
  }, [composeDisclosure.isOpen]);

  return (
    <MailSettingsContext.Provider value={value}>
      {children}
      {composeDisclosure.isOpen && (
        <Suspense fallback={null}>
          <MailCompose
            onCloseCompose={composeDisclosure.onClose}
            defaultValues={defaultComposeValue}
            activeProfileId={activeProfileId}
            hasPermissionForSchoolInbox={hasPermissionForSchoolInbox}
          />
        </Suspense>
      )}
      <SelectMailRecipientTypeModal
        isOpen={!!selectMailRecipientSettings}
        onClose={() => setSelectMailRecipientSettings(undefined)}
        onSubmit={composeEmailToParties}
        settings={
          selectMailRecipientSettings || debouncedSelectMailRecipientSettings
        }
      />
      <MailAdvancedSearchDrawer
        isOpen={advancedSearchDisclosure.isOpen}
        onClose={advancedSearchDisclosure.onClose}
        isSchoolMail={activeProfileId === -1}
        isRulesMail={activeProfileId === -2}
      />
    </MailSettingsContext.Provider>
  );
}

export function useMailSettings() {
  const context = useContext(MailSettingsContext);
  if (context === undefined) {
    throw new Error(
      'useMailSettings must be used within a MailSettingsContext',
    );
  }
  return context;
}
